import { TZDate, tz } from '@date-fns/tz'
import { addDays, differenceInCalendarDays, format, parseISO, startOfDay } from 'date-fns'
import { z } from 'zod'

/**
 * Formats a Date as "HH:MM" in the given IANA timezone, suitable for `<input type="time">`.
 */
export function toWallClockTime(date: Date, timeZone: string): string {
  return format(date, 'HH:mm', { in: tz(timeZone) })
}

/**
 * Formats a Date as "YYYY-MM-DD" in the given IANA timezone, suitable for `<input type="date">`.
 */
export function toWallClockDate(date: Date, timeZone: string): string {
  return format(date, 'yyyy-MM-dd', { in: tz(timeZone) })
}

/** One end of a wall-clock range: the day it names and the time on that day. */
export type WallClockPoint = {
  date: string
  time: string
}

/**
 * The instant a wall-clock date + time names in the given IANA timezone.
 *
 * Useful when form inputs and schedules are written in a clinic's local terms and
 * you need the moment they refer to.
 *
 * Two `TZDate` traps this spelling avoids:
 *
 * 1. The *string* constructor — `new TZDate('2026-03-08T03:30', zone)` — parses
 *    the string in the **system** zone, not the target zone, despite its doc
 *    comment. Only the numeric constructor takes the zone as the interpretation
 *    frame, which is why the parts are split out by hand here.
 * 2. `TZDate.prototype.toISOString()` is overridden to emit a zoned offset
 *    (`...-04:00`) rather than a `Z` string, so the result is re-wrapped in a
 *    plain `Date`.
 *
 * A wall-clock time that does not exist (e.g. 02:30 on a spring-forward day) maps
 * forward to the post-transition offset. Slot-picker-driven callsites cannot
 * produce one — slots come from clinic operating hours, which don't span the
 * transition window — but the appointment and blocked-slot admin forms take a
 * free-text time input with no clinic-hours bound, so a staff member can submit
 * one directly.
 */
export function wallClockToUtc(date: string, time: string, timeZone: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  const [hours, minutes, seconds = 0] = time.split(':').map(Number)
  return new Date(TZDate.tz(timeZone, year, month - 1, day, hours, minutes, seconds).getTime())
}

/**
 * The interval a wall-clock start and end name in the given IANA timezone.
 *
 * Scheduling code works in ranges, not points: a shift, a booking, a blocked slot.
 * The `{ start, end }` shape is what date-fns interval helpers such as
 * `areIntervalsOverlapping` consume directly.
 *
 * Each end carries its own date because the pair is not always same-day — a
 * blocked slot spans `startDate` to `endDate`, and a day-block ends at the *next*
 * day's midnight so the bound is exclusive.
 */
export function wallClockInterval(
  start: WallClockPoint,
  end: WallClockPoint,
  timeZone: string
): { start: Date; end: Date } {
  return {
    start: wallClockToUtc(start.date, start.time, timeZone),
    end: wallClockToUtc(end.date, end.time, timeZone),
  }
}

/**
 * `wallClockToUtc` as a UTC ISO string, for the callers that need one — FullCalendar
 * event props and API payloads, which take a string rather than a `Date`.
 */
export function wallClockToUtcIso(date: string, time: string, timeZone: string): string {
  return wallClockToUtc(date, time, timeZone).toISOString()
}

/**
 * Returns start-of-today in the given IANA timezone as a UTC `Date`.
 *
 * Useful as a cutoff for splitting upcoming vs past records: anything
 * with a timestamp >= the returned value falls on "today" or later in
 * the given timezone.
 *
 * The result is re-wrapped in a plain `Date`: `startOfDay` with a `tz` context
 * returns a `TZDate`, whose `toISOString()` emits a zoned offset rather than a
 * `Z` string. Callers serialise this value, so it must be a plain `Date`.
 */
export function startOfDayUtc(timeZone: string, now: Date = new Date()): Date {
  return new Date(startOfDay(now, { in: tz(timeZone) }).getTime())
}

/**
 * Signed difference in whole calendar days between a timestamp and `now`, as seen
 * in the given IANA timezone. Positive is later, 0 is the same day.
 *
 * Delegates to date-fns `differenceInCalendarDays` with a zone context, which
 * compares calendar days rather than elapsed milliseconds. That matters because
 * a local day is 23 or 25 hours long across a DST transition, so a flat division
 * by 86_400_000 can report a day's difference between two moments on the same day.
 *
 * `now` is injectable for testing.
 */
export function wallClockDayDiff(
  value: Date | string,
  timeZone: string,
  now: Date = new Date()
): number {
  return differenceInCalendarDays(value, now, { in: tz(timeZone) })
}

/**
 * Shifts a YYYY-MM-DD calendar date by `n` days, returning YYYY-MM-DD.
 *
 * Named `addCalendarDays` rather than `addDays` because `date-fns` exports an
 * `addDays` that takes a `Date`. Both are used in this repo; the names must differ.
 *
 * `parseISO` reads the string as local midnight and `format` reads local parts
 * back, so the value never becomes an instant and no timezone can shift it.
 */
export function addCalendarDays(date: string, n: number): string {
  return format(addDays(parseISO(date), n), 'yyyy-MM-dd')
}

/**
 * Formats an **instant** — a moment in time — as a date, by asking which calendar
 * day that moment fell on in the given timezone. Shifting the day is the point:
 * 02:30 UTC really was the previous evening in New Jersey.
 *
 * For a value that names a *day* rather than a moment (a stock expiry, a date of
 * birth), use `formatCalendarDate` — converting those between zones is always
 * wrong. The two are indistinguishable by output; what differs is what the input
 * means, so the choice has to be made deliberately at the callsite.
 *
 * Variants:
 *   - `'short'` → "23 Mar 2025"
 *   - `'long'`  → "Wednesday, 23 Mar 2025"
 *
 * `fmt` is required — `fmt.timeZone` renders the date in a specific timezone,
 * `fmt.locale` in a region's own convention (en-US gives "Mar 23, 2025" rather
 * than the en-GB "23 Mar 2025"). There is deliberately no default: a formatter
 * that silently picks a region is how records end up rendered in the wrong zone.
 * Resolve one `fmt` with `resolveDateFormat` (or `useViewerFormat` in staff UI)
 * rather than passing `timeZone`/`locale` by hand.
 */
export function formatInstantDate(
  value: Date | string,
  fmt: DateFormat,
  style: 'short' | 'long' | 'numeric' = 'short'
): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return date.toLocaleDateString(fmt.locale, {
    ...(style === 'long' ? { weekday: 'long' as const } : {}),
    day: style === 'numeric' ? '2-digit' : 'numeric',
    month: style === 'numeric' ? '2-digit' : 'short',
    year: 'numeric',
    timeZone: fmt.timeZone,
  })
}

/**
 * Formats a *calendar date* — a day, not a moment — e.g. "15 Jun 2027", or
 * "Jun 15, 2027" with an en-US locale.
 *
 * Calendar dates have no timezone: a stock expiry or a date of birth names the
 * same day everywhere. Converting one between zones is always wrong, so
 * `formatInstantDate` should not be used for these. The day is read from the
 * value as written and re-rendered locally, so no offset can shift it: the
 * leading `YYYY-MM-DD` of a string, or the local parts of a `Date`. Note that a
 * string must therefore carry the *intended* day — a value stored as
 * local-midnight-converted-to-UTC does not. Recover it first with
 * `formatCalendarDate(toWallClockDate(instant, entryTimezone), fmt)`, reading the
 * day back in the zone it was written in.
 *
 * Only the locale is region-dependent here — date order is presentation, so it
 * follows the viewer, same as for instants.
 *
 * Takes a full `DateFormat` but reads only `locale` from it: calendar dates have
 * no timezone, and a day named "15 Jun 2027" is the same day everywhere. The
 * parameter is the whole struct anyway so that a caller can resolve one
 * `DateFormat` per render and pass it to every formatter without deciding, at
 * each call, which fields apply.
 */
export function formatCalendarDate(
  value: Date | string,
  fmt: DateFormat,
  style: 'short' | 'long' = 'short'
): string {
  // A `Date` is read via its local parts, not `toISOString()`: date-fns `parseISO` —
  // the codebase's calendar-date parser — returns local midnight, whose UTC day is
  // the previous one anywhere east of Greenwich.
  const [year, month, day] =
    value instanceof Date
      ? [value.getFullYear(), value.getMonth() + 1, value.getDate()]
      : value.slice(0, 10).split('-').map(Number)
  const local = new Date(year, month - 1, day)
  return local.toLocaleDateString(fmt.locale, {
    ...(style === 'long' ? { weekday: 'long' as const } : {}),
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Formats a date or ISO string as "10:00" (or "10:00 AM" with `hour12`).
 *
 * `fmt` is required — `fmt.timeZone` renders the time in a specific timezone,
 * `fmt.locale` / `fmt.hour12` follow a region's clock convention. There is
 * deliberately no default: a formatter that silently picks a region is how
 * records end up rendered in the wrong clock. Resolve one `fmt` with
 * `resolveDateFormat` (or `useViewerFormat` in staff UI) rather than passing
 * `timeZone`/`locale`/`hour12` by hand.
 */
export function formatTime(value: Date | string, fmt: DateFormat): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return date.toLocaleTimeString(fmt.locale, {
    // 12-hour clocks read better without a leading zero ("9:30 PM", not "09:30 PM").
    hour: fmt.hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12: fmt.hour12,
    timeZone: fmt.timeZone,
  })
}

/**
 * Everything needed to render a timestamp: which zone decides the day and
 * clock reading, and which locale decides the date order and 12/24-hour form.
 *
 * Resolve one of these per render with `resolveDateFormat` and pass it to every
 * formatter, rather than threading a clinic id and a region through each call.
 */
export type DateFormat = {
  timeZone: string
  locale: string
  hour12: boolean
}

/**
 * Renders how far ahead a future day is — "in 3 days", "in 5 weeks", "in 4 months".
 *
 * The unit widens with distance so the number stays small enough to read at a
 * glance; "in 5 weeks" lands faster than "in 36 days".
 *
 * Takes the day count rather than computing it — `wallClockDayDiff` already
 * does that arithmetic (DST-safely), and duplicating it here risked drifting.
 */
function formatDayDistance(days: number, locale: string): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' })
  if (days < 14) return rtf.format(days, 'day')
  if (days < 60) return rtf.format(Math.round(days / 7), 'week')
  return rtf.format(Math.round(days / 30), 'month')
}

/** A timeline date-group header: what it reads, and whether the day is still ahead. */
export type DayGroup = {
  label: string
  isFuture: boolean
}

/**
 * Resolves a timeline date-group header — "Today", "Yesterday", "Tomorrow", a
 * future date with its distance ("29 Aug 2026 · in 5 weeks"), or a plain date —
 * together with whether that day is still ahead.
 *
 * Both answers come from one comparison, which is the point: the header and the
 * "upcoming" treatment of the entries beneath it cannot disagree. They previously
 * could, because the flag was computed separately in each *entry's clinic* zone
 * while the header used the viewer's, so a group headed "Tomorrow" could hold
 * entries not marked upcoming.
 *
 * Takes the frame the entry renders in rather than a region — its clinic's zone for
 * a clinic-bound record, the viewer's otherwise — so a header lands in the same
 * frame as the entry beneath it and the two can never name different days.
 *
 * `now` is injectable for testing. Comparing calendar days rather than
 * subtracting 24h keeps "Yesterday" right across DST, where a local day is 23
 * or 25 hours long.
 */
export function resolveDayGroup(
  value: Date | string,
  fmt: DateFormat,
  now: Date = new Date()
): DayGroup {
  const { timeZone, locale } = fmt
  const entryDay = toWallClockDate(typeof value === 'string' ? new Date(value) : value, timeZone)
  const today = toWallClockDate(now, timeZone)
  // YYYY-MM-DD sorts lexicographically the same way it sorts chronologically.
  const isFuture = entryDay > today

  if (entryDay === today) return { label: 'Today', isFuture }
  if (addCalendarDays(entryDay, 1) === today) return { label: 'Yesterday', isFuture }
  if (entryDay === addCalendarDays(today, 1)) return { label: 'Tomorrow', isFuture }

  const date = formatInstantDate(value, fmt)
  // Beyond tomorrow a bare date reads as history at a glance, so a future header
  // states the distance too.
  if (isFuture) {
    return {
      label: `${date} · ${formatDayDistance(wallClockDayDiff(value, timeZone, now), locale)}`,
      isFuture,
    }
  }
  return { label: date, isFuture }
}

/** A timestamp on the wire: handlers may return a `Date`, clients always receive an ISO string. */
export const isoDateTime = z
  .union([z.date(), z.iso.datetime({ offset: true })])
  .transform((value) => (typeof value === 'string' ? value : value.toISOString()))
  .pipe(z.iso.datetime({ offset: true }))

/** A day with no time zone (`YYYY-MM-DD`). Never a `Date`: formatting one in a browser's zone can shift the day. */
export const calendarDate = z.iso.date()
